import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  type TextFieldProps,
  useMediaQuery,
  useTheme,
  Typography as MuiTypography,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import { forwardRef, useEffect } from 'react';
import Form from '../../libs/atoms/Form';
import Typography from '../../libs/atoms/Typography';
import RequiredText from '../../libs/atoms/RequiredText';
import { LoadingButton } from '@mui/lab';
import { DevTool as HookFormDevTool } from '@hookform/devtools';
import type EligibilityWidgetFormData from '../../../types/eligibilityWidget/EligibilityWidgetFormData';
import useEligibilityWidgetForm from '../hooks/useEligibilityWidgetForm';
import useTranslation from '../../states/translation/useTranslation';
import useAppConfig from '../../states/config/useAppConfig';
import { isDesktopScreen } from '../../libs/utils/screenSizes/screenSizes';
import type { CreateEligibilityWidgetTokenResponse } from '../queries/CREATE_ELIGIBILITY_WIDGET_TOKEN';
import pgPrivacyLink from '../../libs/configs/pgPrivacyLink';
import { PartnerType } from '../../../types/Configuration';
// eslint-disable-next-line no-undef
const isProduction = process.env.NODE_ENV === 'PRODUCTION';

const MIN_LENGTH_PID = 6;
const MAX_LENGTH_PID = 20;
const MIN_LENGTH_PNR_DEFAULT = 5;
const MAX_LENGTH_PNR_DEFAULT = 6;
const MIN_LENGTH_PNR_CRUISE = 3;
const MAX_LENGTH_PNR_CRUISE = 20;
const MIN_LENGTH_LAST_NAME = 2;
const MAX_LENGTH_LAST_NAME = 40;
const ALPHA_NUMERIC = /^[\dA-Za-z]+$/;
const ALPHA_NUMERIC_SEPARATORS = /^[\w-]+$/;

interface EligibilityWidgetFormProps {
  onSubmit: (data: EligibilityWidgetFormData) => Promise<CreateEligibilityWidgetTokenResponse>;
  dataTestId?: string;
  pid: string;
  apiKey: string;
}

export const makeDataTestId = (dataTestId = 'eligibility-widget') => {
  return {
    prefix: dataTestId,
    wrapper: `${dataTestId}.form.wrapper`,
    partnerSelect: `${dataTestId}.form.partner.select`,
    pidValue: `${dataTestId}.form.pid.value`,
    apiKeyValue: `${dataTestId}.form.apiKey.value`,
    pnrInput: `${dataTestId}.form.pnr.input`,
    lastNameInput: `${dataTestId}.form.lastname.input`,
    privacyPolicyValue: `${dataTestId}.form.privacyPolicy.value`,
    privacyPolicyLink: `${dataTestId}.form.privacyPolicy.link`,
  };
};

const HiddenTextField = forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => (
  <Box hidden>
    <FormControl>
      <TextField type="hidden" disabled {...props} ref={ref} />
    </FormControl>
  </Box>
));

const EligibilityWidgetForm: React.FC<EligibilityWidgetFormProps> = ({ onSubmit, dataTestId, pid, apiKey }) => {
  const allDataTestId = makeDataTestId(dataTestId);
  const { affiliatePartners: partners, partnerType, up } = useAppConfig().states.config;
  const theme = useTheme();
  const isDesktop = useMediaQuery(isDesktopScreen(theme));
  const {
    register,
    control,
    handleSubmit,
    getValidator,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useEligibilityWidgetForm();
  const { t } = useTranslation().actions;

  const getAffiliateApiKey = () =>
    partners?.find((partner) => partner.enabled && partner.pid === getValues('pid'))?.eligibilityWidgetApiKey ?? '';

  /**
   * React-hook-form does not update value after re-render
   * Update @pid && @apiKey when props change
   */
  useEffect(() => {
    setValue('pid', pid);
    setValue('apiKey', apiKey);
  }, [pid, apiKey]);

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)} noValidate data-testid={allDataTestId.wrapper}>
        <Grid container columnSpacing={2} rowSpacing={2} alignItems="flex-start">
          <Grid item xs={12} marginBottom={1}>
            <Typography variant="subtitle3-bold">{t('eligibility-widget.form.header')}</Typography>
          </Grid>
          {partnerType === PartnerType.affiliate ? (
            <Grid item xs={12} md>
              <FormControl fullWidth>
                <FormLabel>
                  <RequiredText>
                    <Typography variant="body2-bold">{t('eligibility-widget.form.partner.placeholder')}</Typography>
                  </RequiredText>
                </FormLabel>
                <Select
                  defaultValue=""
                  displayEmpty
                  aria-errormessage={errors?.pid?.message}
                  data-testid={allDataTestId.partnerSelect}
                  {...register('pid', {
                    ...getValidator('eligibility-widget.form.partner.placeholder', {
                      minLength: MIN_LENGTH_PID,
                      maxLength: MAX_LENGTH_PID,
                      pattern: ALPHA_NUMERIC,
                    }),
                    onChange: () => setValue('apiKey', getAffiliateApiKey()),
                  })}
                  disabled={isSubmitting}
                >
                  <MenuItem value="">{t('eligibility-widget.form.select.default-value')}</MenuItem>
                  {partners?.map(
                    (partner) =>
                      partner.enabled && (
                        <MenuItem key={partner.pid} value={partner.pid} data-testid={allDataTestId.pidValue}>
                          {partner.partnerName}
                        </MenuItem>
                      ),
                  )}
                </Select>
                <FormHelperText error={Boolean(errors?.pid)}>{errors?.pid?.message}</FormHelperText>
              </FormControl>
              <HiddenTextField data-testid={allDataTestId.apiKeyValue} {...register('apiKey')} />
            </Grid>
          ) : (
            <>
              <HiddenTextField data-testid={allDataTestId.pidValue} {...register('pid', { value: pid })} />
              <HiddenTextField data-testid={allDataTestId.apiKeyValue} {...register('apiKey', { value: apiKey })} />
            </>
          )}
          <Grid item xs={12} md>
            <FormControl fullWidth>
              <FormLabel>
                <RequiredText>
                  <Typography variant="body2-bold">{t('eligibility-widget.form.pnr.placeholder')}</Typography>
                </RequiredText>
              </FormLabel>
              <TextField
                error={Boolean(errors?.pnr)}
                aria-errormessage={errors?.pnr?.message}
                helperText={errors?.pnr?.message}
                data-testid={allDataTestId.pnrInput}
                {...register('pnr', {
                  ...getValidator('eligibility-widget.form.pnr.placeholder', {
                    minLength: partnerType === PartnerType.cruise ? MIN_LENGTH_PNR_CRUISE : MIN_LENGTH_PNR_DEFAULT,
                    maxLength: partnerType === PartnerType.cruise ? MAX_LENGTH_PNR_CRUISE : MAX_LENGTH_PNR_DEFAULT,
                    pattern: partnerType === PartnerType.cruise ? ALPHA_NUMERIC_SEPARATORS : ALPHA_NUMERIC,
                  }),
                })}
                disabled={isSubmitting}
              ></TextField>
            </FormControl>
          </Grid>
          <Grid item xs={12} md>
            <FormControl fullWidth>
              <FormLabel>
                <RequiredText>
                  <Typography variant="body2-bold">{t('eligibility-widget.form.last-name.placeholder')}</Typography>
                </RequiredText>
              </FormLabel>
              <TextField
                error={Boolean(errors?.lastName)}
                aria-errormessage={errors?.lastName?.message}
                helperText={errors?.lastName?.message}
                data-testid={allDataTestId.lastNameInput}
                {...register('lastName', {
                  ...getValidator('eligibility-widget.form.last-name.placeholder', (fieldname) => {
                    return {
                      minLength: MIN_LENGTH_LAST_NAME,
                      maxLength: MAX_LENGTH_LAST_NAME,
                      validate: {
                        whitespaces: (value: string) =>
                          value.trim() === value ? true : t('eligibility-widget.form.invalid-dynamic', t(fieldname)),
                      },
                    };
                  }),
                })}
                disabled={isSubmitting}
              ></TextField>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            {up?.componentConfigs.eligibilityWidget?.showPrivacyPolicyCheckbox ? (
              <FormControl fullWidth>
                <FormControlLabel
                  label={
                    <MuiTypography component="span" sx={{ fontSize: '0.84rem', fontStyle: 'italic' }}>
                      {t('eligibility-widget.form.privacy-policy.body', pgPrivacyLink)}{' '}
                      <Link
                        href={pgPrivacyLink}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={allDataTestId.privacyPolicyLink}
                        onClick={(event) => event.stopPropagation()}
                        color={theme.palette.link}
                      >
                        {t('eligibility-widget.form.privacy-policy.link')}
                      </Link>
                    </MuiTypography>
                  }
                  control={
                    <Checkbox
                      data-test-id={allDataTestId.privacyPolicyValue}
                      {...register('privacyPolicy', {
                        ...getValidator('eligibility-widget.form.privacy-policy.placeholder'),
                      })}
                    />
                  }
                />
                <FormHelperText error={Boolean(errors?.privacyPolicy)} data-testid={allDataTestId.privacyPolicyValue}>
                  {errors?.privacyPolicy?.message}
                </FormHelperText>
              </FormControl>
            ) : (
              <MuiTypography component="span" sx={{ fontSize: '0.84rem', fontStyle: 'italic' }}>
                {t('eligibility-widget.form.privacy-policy.body', pgPrivacyLink)}{' '}
                <Link
                  href={pgPrivacyLink}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={allDataTestId.privacyPolicyLink}
                  onClick={(event) => event.stopPropagation()}
                  color={theme.palette.link}
                >
                  {t('eligibility-widget.form.privacy-policy.link')}
                </Link>
              </MuiTypography>
            )}
          </Grid>
          <Grid item xs={12} marginTop={1}>
            <LoadingButton
              variant="contained"
              fullWidth={!isDesktop}
              color="primary"
              type="submit"
              loading={isSubmitting}
            >
              <Typography variant="body2-bold">{t('eligibility-widget.form.submit-button')}</Typography>
            </LoadingButton>
          </Grid>
        </Grid>
      </Form>
      {!isProduction && <HookFormDevTool control={control} />}
    </>
  );
};

export default EligibilityWidgetForm;
